import { Link } from "react-router-dom";
import { LOGO_SRC } from "../data/landing";

/**
 * Minimal layout used for standalone legal pages
 * (Privacy Policy, Terms of Use, Refund Policy).
 *
 * - White background, clean Inter typography
 * - eGeez logo top-left links back to /for-fans
 * - Small copyright footer
 * - Fully mobile responsive
 *
 * NOTE: this layout deliberately does NOT render the marketing
 *       NavBar / NewsTicker / Footer — legal pages should feel like
 *       quiet, trustworthy documents.
 */
export default function LegalLayout({ title, lastUpdated, children }) {
  return (
    <div className="min-h-screen bg-white text-black flex flex-col">
      {/* Minimal top bar */}
      <header className="border-b border-black/10 bg-white sticky top-0 z-40 backdrop-blur-md bg-white/85">
        <div className="mx-auto max-w-[920px] px-5 md:px-8 h-[68px] md:h-[76px] flex items-center justify-between">
          <Link
            to="/for-fans"
            className="flex items-center"
            data-testid="legal-logo-home-link"
            aria-label="eGeez — back to home"
          >
            <img
              src={LOGO_SRC}
              alt="eGeez"
              className="h-8 md:h-10 w-auto"
              draggable={false}
            />
          </Link>
          <Link
            to="/for-fans"
            className="text-[13px] md:text-[14px] font-medium text-black/65 hover:text-[#0041E6] transition-colors"
          >
            ← Back to home
          </Link>
        </div>
      </header>

      {/* Document body */}
      <main className="flex-1">
        <article className="mx-auto max-w-[820px] px-5 md:px-8 py-10 md:py-16 legal-doc">
          <div className="text-[11px] tracking-[0.32em] uppercase text-[#0041E6] font-bold mb-4">
            Legal
          </div>
          <h1
            className="text-black tracking-tight"
            style={{
              fontFamily:
                '"Inter", "Helvetica Neue", system-ui, sans-serif',
              fontWeight: 800,
              fontSize: "clamp(32px, 4.4vw, 52px)",
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
            }}
          >
            {title}
          </h1>
          {lastUpdated && (
            <p className="mt-3 text-[13px] md:text-[14px] text-black/55">
              {lastUpdated}
            </p>
          )}
          <div className="mt-8 h-px bg-black/10" aria-hidden />
          <div className="mt-8 legal-content">{children}</div>
        </article>
      </main>

      {/* Minimal copyright footer */}
      <footer className="border-t border-black/10 mt-10">
        <div className="mx-auto max-w-[920px] px-5 md:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[12px] md:text-[13px] text-black/55">
          <span>© 2026 eGeez Inc.™ · All rights reserved</span>
          <div className="flex items-center gap-4">
            <Link to="/privacy-policy" className="hover:text-[#0041E6] transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-[#0041E6] transition-colors">Terms of Use</Link>
            <Link to="/refund-policy" className="hover:text-[#0041E6] transition-colors">Refund Policy</Link>
          </div>
        </div>
      </footer>

      {/* Document typography rules (scoped) */}
      <style>{`
        .legal-content { font-family: "Inter", "Helvetica Neue", system-ui, sans-serif; color: #1A1A1A; }
        .legal-content h2 { font-size: clamp(20px, 2.4vw, 26px); font-weight: 700; letter-spacing: -0.01em; margin-top: 2.5rem; margin-bottom: 0.75rem; line-height: 1.25; color: #0B0B0F; }
        .legal-content h3 { font-size: 16px; font-weight: 700; margin-top: 1.5rem; margin-bottom: 0.5rem; color: #0B0B0F; }
        .legal-content p  { font-size: 15.5px; line-height: 1.75; color: rgba(0,0,0,0.78); margin-bottom: 1rem; }
        .legal-content ul, .legal-content ol { padding-left: 1.4rem; margin-bottom: 1rem; }
        .legal-content li { font-size: 15.5px; line-height: 1.75; color: rgba(0,0,0,0.78); margin-bottom: 0.35rem; }
        .legal-content strong { color: #0B0B0F; font-weight: 700; }
        .legal-content em { color: #0B0B0F; }
        .legal-content a { color: #0041E6; text-decoration: underline; text-underline-offset: 2px; }
        .legal-content a:hover { color: #0033B8; }
        .legal-content hr { margin: 2rem 0; border: 0; border-top: 1px solid rgba(0,0,0,0.1); }
        .legal-content table { width: 100%; border-collapse: collapse; margin: 1rem 0 1.5rem; font-size: 14.5px; }
        .legal-content th, .legal-content td { border: 1px solid rgba(0,0,0,0.12); padding: 10px 14px; text-align: left; vertical-align: top; }
        .legal-content th { background: #FAFAFA; font-weight: 700; color: #0B0B0F; }
        .legal-content blockquote { border-left: 3px solid #0041E6; padding: 0.5rem 1rem; margin: 1rem 0; background: #F6F8FF; color: rgba(0,0,0,0.75); }
        .legal-content .doc-section-num { color: #0041E6; font-weight: 700; margin-right: 0.4rem; }
        @media (max-width: 640px) {
          .legal-content table, .legal-content thead, .legal-content tbody, .legal-content tr, .legal-content th, .legal-content td { display: block; width: 100%; }
          .legal-content th { display: none; }
          .legal-content td { border: none; border-bottom: 1px solid rgba(0,0,0,0.08); padding: 8px 0; }
          .legal-content td:before { content: attr(data-label); font-weight: 700; display: block; color: #0B0B0F; margin-bottom: 4px; font-size: 12.5px; letter-spacing: 0.04em; text-transform: uppercase; }
        }
      `}</style>
    </div>
  );
}
